/**
 * Consumes the `?token=` query param from a password-reset email link and
 * sets a new password. The token is short-lived (30 min) and single-purpose
 * — an invalid or expired one surfaces as an honest, actionable error.
 */

import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { api, ApiError } from '../api'
import { LangSwitch } from '../components/LangSwitch'
import { PivotLogo } from '../components/PivotLogo'
import { ThemeToggle } from '../components/ThemeToggle'
import { useI18n } from '../i18n'
import '../styles/auth-forms.css'
import '../styles/header-actions.css'

export default function ResetPassword() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await api.auth.resetPassword(token, newPassword)
      setSuccess(true)
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 400
          ? t('auth.resetInvalidToken')
          : t('auth.genericError'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="auth-page">
      <title>{`${t('auth.resetTitle')} — ${t('common.appName')}`}</title>
      <header className="auth-bar">
        <Link to="/" className="auth-brand" aria-label={t('a11y.authHomeLink')}>
          <PivotLogo />
          {t('common.appName')}
        </Link>
        <div className="header-actions">
          <LangSwitch />
          <ThemeToggle />
        </div>
      </header>
      <div className="auth-body">
        <div className="auth-card">
          <h1>{t('auth.resetTitle')}</h1>
          {success ? (
            <>
              <p className="form-success">{t('auth.resetSuccess')}</p>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/login')}>
                {t('auth.loginCta')}
              </button>
            </>
          ) : (
            <>
              <p>{t('auth.resetBody')}</p>
              <form onSubmit={handleSubmit} className="stack auth-form">
                <label>
                  {t('auth.newPasswordLabel')}
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </label>
                {error && <p className="form-error">{error}</p>}
                <button type="submit" className="btn btn-primary" disabled={submitting || !token}>
                  {t('auth.resetCta')}
                </button>
              </form>
            </>
          )}
          <p className="auth-links">
            <Link to="/login" className="auth-link">
              {t('auth.backToLogin')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
